import classNames from 'classnames';
import React, { cloneElement, FC, isValidElement, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import Icon from 'src/common/components/IconDS22';
import getClassName from 'src/common/utils/getClassName';
import { getAuthenticatedUserRole, getUser } from 'src/common/utils/reduxStoreSelectors';
import NavDropdown from '../NavDropdown';
import NavModal from '../NavModal';
import useNavigationConfig from './config';
import s from './Navigation.module.scss';
import NavItem from './NavItem';
import ScreenSizeWrapper from './ScreenSizeWrapper';
import SubNavigation from './SubNavigation';
import TopNavItemWrapper from './TopNavItemWrapper';
import type { MainNavItemComponentType, MainNavItemLinkType, MainNavItemType } from './types';

const Navigation: FC = () => {
  const user = useSelector(getUser);
  const userRole = useSelector(getAuthenticatedUserRole);
  const isLoggedIn = user.get('loggedIn');
  const isOwner = user.get('owner_approved');
  const modifiers = `mainNav ${isLoggedIn ? 'authenticated' : 'anonymous'}`;
  const configItems = useNavigationConfig() as MainNavItemType[];

  let menuItems = userRole === 'captain'
    ? configItems.filter(
      (config) => config.isHeaderNavItem?.owner && (config.roles == null || config.roles.includes('captain')),
    )
    : configItems.filter(
      (config) => config.isHeaderNavItem?.renter && (config.roles == null || config.roles.includes('renter')),
    );

  if (isOwner) {
    menuItems = configItems.filter(
      (config) => config.isHeaderNavItem?.owner && (config.roles == null || config.roles.includes('owner')),
    );
  }

  return (
    <nav className={s.root}>
      <ul className={s.menu}>
        {menuItems.map((navItemConfig: MainNavItemComponentType | MainNavItemLinkType) => {
          const screenSizeModifiers = navItemConfig.isHeaderNavItem?.[isOwner ? 'owner' : 'renter']?.join(' ');
          const iconOnlyScreenSizeModifiers = navItemConfig.iconOnlyScreenSizes?.[isOwner ? 'owner' : 'renter']?.map((size) => `iconOnly_${size}`).join(' ');
          const navItemClassNameModifiers = classNames(
            iconOnlyScreenSizeModifiers,
            modifiers,
            screenSizeModifiers,
          );

          if (!screenSizeModifiers) return null;

          let navItemElement: ReactNode = null;
          if ('component' in navItemConfig && navItemConfig.component) {
            // Only support React elements, not component types
            if (isValidElement(navItemConfig.component)) {
              navItemElement = cloneElement(
                navItemConfig.component,
                {
                  ...navItemConfig.component.props,
                  classNameModifier: classNames(
                    navItemConfig.component.props?.classNameModifier,
                    navItemClassNameModifiers,
                  ),
                },
              );
            }
          } else {
            navItemElement = (
              <NavItem
                classNameModifier={navItemClassNameModifiers}
                to={'path' in navItemConfig ? navItemConfig.path : ''}
                icon={navItemConfig.icon}
              >
                {navItemConfig.label ?? ''}
              </NavItem>
            );
          }

          return (
            <TopNavItemWrapper
              key={navItemConfig.key}
              classNameModifier={navItemClassNameModifiers}
            >
              {navItemElement}
            </TopNavItemWrapper>
          );
        })}

        <li className={getClassName(s, 'item', `${modifiers} dropdown`)}>
          <ScreenSizeWrapper screenSizeWrapper="mDown">
            <NavModal
              classNameModifier="menuItems nestedMenu"
              heading={(
                isLoggedIn && (
                  <Icon
                    id="menu"
                    size="l"
                    className={s.hamburger}
                  />
                )
              )}
            >
              <SubNavigation nestedMenuVariant="sidebar" />
            </NavModal>
          </ScreenSizeWrapper>
          <ScreenSizeWrapper screenSizeWrapper="m">
            <NavDropdown
              className={getClassName(s, 'link', modifiers)}
              classNameModifier="nestedMenu"
              data-test="MainMenuDropDown"
              aria-label="Main menu"
              withChevron={false}
              heading={(
                isLoggedIn && (
                  <Icon
                    id="menu"
                    size="l"
                    className={s.hamburger}
                  />
                )
              )}
            >
              <SubNavigation />
            </NavDropdown>
          </ScreenSizeWrapper>
        </li>
      </ul>
    </nav>
  );
};

export default Navigation;
